// Core Modules
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';

// External Modules
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { NgxPermissionsModule, NgxPermissionsService } from 'ngx-permissions';
import { catchError, EMPTY, finalize, from, map, Observable, Subscription, switchMap, tap } from 'rxjs';

// Tokens
import { WINDOW } from '@core/injection-tokens/tokens';

// Services
import { AccessControlService } from '@core/services/access-control/access-control.service';
import { AuthService } from '@core/services/auth/auth.service';
import { ConfigService } from '@core/services/config/config.service';
import { CryptoService } from '@core/services/crypto/crypto.service';
import { EditAudienceService } from '@edit-audience/services/edit-audience.service';
import { SessionManagerService } from '@core/services/session-manager/session-manager.service';
import { UserSettingsService } from '@shared/services/user-settings/user-settings.service';

// Models
import { AppEnvironmentConfig } from '@shared/models/environment-config.model';
import { ClientMarketingAutomationModel } from '@shared/models/client-marketing-automation.model';
import { HeaderContentModel } from '@shared/models/header-content.model';
import { IAuth0ClientInfo, IClientList, IUserProfileBasedOnFields } from '@shared/models/auth0-types.model';
import { IUserSettingsConfiguration } from '@shared/models/user-settings/user-settings-config.model';
import { IUserSettingsUsageReportURL } from '@shared/models/user-settings/user-settings-usage-report.model';

// Enums
import { LocalStorageEnum } from '@shared/enum/local-storage-keys.enum';
import { MainNavigationEnum } from '@shared/enum/main-navigation-url.enum';
import { UserProfileFieldsEnum } from '@shared/enum/user-profile-fields.enum';
import { UserSettingsEnum } from '@shared/enum/user-settings.enum';

// Environment
import { environment } from '@env/environment';

@Component({
  selector: 'app-user-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbDropdownModule, NgxPermissionsModule],
  providers: [DatePipe, UserSettingsService],
  templateUrl: './user-settings.component.html',
  styleUrl: './user-settings.component.scss',
})
export class UserSettingsComponent implements OnInit, OnDestroy {
  // Injectors
  private readonly accessControlService = inject(AccessControlService);
  private readonly authService = inject(AuthService);
  private readonly configService = inject(ConfigService);
  private readonly cryptoService = inject(CryptoService);
  private readonly datePipe = inject(DatePipe);
  private readonly editAudienceService = inject(EditAudienceService);
  private readonly ngxPermissionsService = inject(NgxPermissionsService);
  private readonly router = inject(Router);
  private readonly sessionManagerService = inject(SessionManagerService);
  private readonly userSettingsService = inject(UserSettingsService);
  private readonly window = inject(WINDOW);

  // Private Members
  private readonly appEnvironment: AppEnvironmentConfig;
  private readonly subscription: Subscription = new Subscription();
  private userDomain = 'https://auth0.exp-dev.mercuryhealthcare.com';
  private userProfile?: IUserProfileBasedOnFields;

  // Public Members
  public readonly apiDocumentationRoute: string = `/${MainNavigationEnum.DOCS}`;
  public readonly headerContents: HeaderContentModel;
  public readonly userSettingsEndpoints: IUserSettingsConfiguration;
  public clientList: IAuth0ClientInfo[] = [];
  public currentAppPermissions?: string | string[];
  public dXeHelpEnabled = false;
  public isConsoleLogin: boolean = false;
  public legacyHelpDesk = false;
  public marketingAutomationUrl: string = '';
  public reportCreatedDate: string = '';
  public reportCreatedOn: string = '';
  public selectedClient?: string;
  public switchClientStatusIndicator = false;
  public usageReportDownloadUrl: string;
  public userEmailName?: string;
  public userNameInitials?: string;

  constructor() {
    // Load the header static contents from app-contents.json file
    this.headerContents = this.configService.getContents('header');
    this.userSettingsEndpoints = this.configService.userSettingsEndpoints;
    this.usageReportDownloadUrl = '';
    this.appEnvironment = environment;
    this.userDomain = localStorage.getItem(LocalStorageEnum.AUTH0_DOMAIN) as string;
  }

  public ngOnInit(): void {
    this.userProfile = this.authService.getUserProfileBasedOnFields([UserProfileFieldsEnum.EMAIL, UserProfileFieldsEnum.HG_CUSTOMER]);
    this.userEmailName = this.userProfile?.email ? this.userProfile.email : 'NOT_AVAILABLE';
    this.getUserNameInitials();
    const auth0ConnectionName = localStorage.getItem(LocalStorageEnum.CONNECTION);
    if (auth0ConnectionName) {
      this.isConsoleLogin = environment.AUTH0.consoleConnection === this.cryptoService.decrypt(auth0ConnectionName);
    }
    this.subscription.add(
      this.authService.getClients().subscribe({
        next: (clients: IClientList) => {
          this.clientList = clients.items;
          if (this.clientList.length === 1) {
            this.prepareDropDownOptions();
          }
        },
        error: (error: HttpErrorResponse) => {
          console.log(`<UserSettingsComponent> - <ngOnInit> Error occurred during get clients...`, error);
          this.sessionManagerService.stopWatching();
          this.authService.logout();
        },
      })
    );
    this.getHelpCenterLinks();
  }

  /**
   * Get Client's Marketing Automation Preference (API Call) and generate login URL for MA.
   * TODO : SSO/SAML integration for SFMC/SFHC. Now static login URL's configured for SFMC/SFHC
   */
  private getClientMAUrl(): void {
    this.ngxPermissionsService.hasPermission('client-ma-preference-read').then(
      (isPermissionValid: boolean) => {
        if (isPermissionValid) {
          this.subscription.add(
            this.userSettingsService.getMarketingAutomationConfig().subscribe({
              next: (response: ClientMarketingAutomationModel[]) => {
                let clientMA: string = '';
                if (response.length) {
                  const clientMADetail: ClientMarketingAutomationModel[] = response.filter((ma: ClientMarketingAutomationModel) => ma.isDeleted === 0);
                  if (clientMADetail.length && clientMADetail[0].externalSourceName) {
                    clientMA = clientMADetail[0].externalSourceName.toUpperCase();
                  }
                  switch (clientMA) {
                    case 'ELOQUA':
                      this.marketingAutomationUrl = this.userSettingsService.getEloquaURL();
                      break;
                    case 'SFMC':
                      this.marketingAutomationUrl = this.appEnvironment.SFMC_LOGIN_URL;
                      break;
                    case 'SFHC':
                      this.marketingAutomationUrl = this.appEnvironment.SFHC_LOGIN_URL;
                      break;
                    default:
                      this.marketingAutomationUrl = '';
                  }
                } else {
                  this.marketingAutomationUrl = '';
                }
              },
              error: (e) => {
                console.log(`<UserSettingsComponent> - <getClientMAUrl> Error occurred during subscribing Client MA URL...`, e);
                this.switchClientStatusIndicator = false;
              },
              complete: () => (this.switchClientStatusIndicator = false),
            })
          );
        } else {
          this.switchClientStatusIndicator = false;
        }
      },
      () => {
        this.switchClientStatusIndicator = false;
      }
    );
  }

  private getHelpCenterLinks(): void {
    const todayDate = new Date().getTime();
    const sunsetDate = this.configService.dXeEnableDate();
    this.dXeHelpEnabled = sunsetDate.getTime() < todayDate; // if true display old else display New
    const preSunsetDate = sunsetDate.setDate(sunsetDate.getDate() - 4);
    this.legacyHelpDesk = (this.userEmailName && this.userEmailName.indexOf('mercuryhealthcare.com') > -1 && !this.dXeHelpEnabled && preSunsetDate <= todayDate) || false;
    this.userSettingsEndpoints.dXeHelpURL = `https://${this.userDomain}${this.userSettingsEndpoints.dXeHelpURL}`;
  }

  /**
   * This method is used to get usage report url
   */
  private getUsageReportUrl(): void {
    this.switchClientStatusIndicator = true;
    this.subscription.add(
      this.userSettingsService.getUsageReportUrl().subscribe({
        next: (usageReportURL: IUserSettingsUsageReportURL) => {
          if (typeof usageReportURL === 'object' && Object.keys(usageReportURL).length > 0) {
            this.usageReportDownloadUrl = usageReportURL.url;
            if (usageReportURL.generatedReportDate) {
              const reportDate = usageReportURL.generatedReportDate.replace(/(\d+)(st|nd|rd|th)/, '$1');
              this.reportCreatedDate = this.datePipe.transform(reportDate, UserSettingsEnum.DATE_FORMAT) || '';
            }
          } else {
            this.usageReportDownloadUrl = '';
          }
        },
        error: (e) => {
          this.usageReportDownloadUrl = '';
          this.switchClientStatusIndicator = false;
          console.log(`<UserSettingsComponent> - <getUsageReportUrl> Error occurred during subscribing getUsageReportUrl...`, e);
        },
        complete: () => (this.switchClientStatusIndicator = false),
      })
    );
  }

  /**
   * get initials of user name for profile photo
   */
  private getUserNameInitials(): void {
    if (this.userEmailName && this.userEmailName !== 'NOT_AVAILABLE') {
      this.userNameInitials = this.userEmailName.charAt(0).toUpperCase();
    }
  }

  private performClientChange(): Observable<boolean> {
    this.switchClientStatusIndicator = true;
    this.marketingAutomationUrl = '';
    this.usageReportDownloadUrl = '';

    const clientDetail = this.clientList.find((client) => client.clientName === this.selectedClient);
    if (!clientDetail) {
      console.error('<performClientChange> Selected client not found.');
      this.switchClientStatusIndicator = false;
      return EMPTY;
    }

    this.authService.updateClientInfo(clientDetail);
    this.authService.clientNavigationInProgressSubject.next(true);
    this.authService.authenticationInProgressSubject.next(true);

    return this.accessControlService.definePermissions().pipe(
      map(() => undefined),
      tap(() => {
        this.authService.sendDataToGainSights();
        this.validatePermAndFetchUsageReportUrl();
        this.getClientMAUrl();
      }),
      switchMap(() => from(this.accessControlService.getLandingPageBasedOnUserScopes()).pipe(switchMap((landingPage) => from(this.router.navigate([landingPage])).pipe(tap(() => this.window.location.reload()))))),
      finalize(() => {
        this.authService.clientNavigationInProgressSubject.next(false);
        this.authService.authenticationInProgressSubject.next(false);
        this.switchClientStatusIndicator = false;
      }),
      catchError((error) => {
        console.error('<performClientChange> Error in definePermissions chain:', error);
        this.authService.clientNavigationInProgressSubject.next(false);
        this.authService.authenticationInProgressSubject.next(false);
        this.switchClientStatusIndicator = false;
        return EMPTY;
      })
    );
  }

  private prepareDropDownOptions(): void {
    const userProfile = JSON.parse(localStorage.getItem(LocalStorageEnum.USER_PROFILE) || '{}');
    if (userProfile['hg-customer'] && userProfile['hg-customer']?.clientName) {
      this.selectedClient = this.cryptoService.decrypt(userProfile['hg-customer'].clientName.toString());
    }
    this.getClientMAUrl();
    // validate the permission.
    this.validatePermAndFetchUsageReportUrl();
  }

  private validatePermAndFetchUsageReportUrl(): void {
    this.usageReportDownloadUrl = '';
    this.ngxPermissionsService.hasPermission('user-settings-usage-report').then((isPermissionValid: boolean) => {
      if (isPermissionValid) {
        this.getUsageReportUrl();
      } else {
        this.usageReportDownloadUrl = '';
      }
    });
  }

  /**
   * Describes the client change call which include changing hg-client data on local storage and initializing gain insights for respective client and user
   */
  public changeClient(): void {
    const clientChange$ = this.editAudienceService.isCriteriaUnsaved
      ? from(this.editAudienceService.openUnsavedAudienceModal()).pipe(
          switchMap((result) => {
            if (result === 'success') {
              this.editAudienceService.isCriteriaUnsaved = false;
              return this.performClientChange();
            }
            console.info('Client change cancelled');
            return EMPTY;
          }),
          catchError(() => {
            console.info('Client change modal rejected');
            return EMPTY;
          })
        )
      : this.performClientChange();

    this.subscription.add(clientChange$.subscribe());
  }

  /**
   * This method is used to set href download usage report url and download report
   */
  public downloadUsageReport(): void {
    const link = document.createElement('a');
    link.href = this.usageReportDownloadUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  public initUserSettingsDropDownOptions(open: boolean): void {
    if (!open || this.clientList.length === 1) return;
    this.prepareDropDownOptions();
  }

  /**
   * This method is used for logging out from Auth0.
   *
   * @memberof HeaderComponent
   */
  public logout(): void {
    this.sessionManagerService.stopWatching();
    this.authService.logout();
  }

  public navigateToFeedbackPortal(): void {
    window.open('assets/product-feedback-portal.html');
  }

  public navigateToHelpCenter(): void {
    this.ngxPermissionsService.hasPermission('support-center-access').then((isPermissionValid: boolean) => {
      const isCustomDomain: number = +(this.userDomain === this.configService.auth0Domain);
      const queryParam = `is-sf-user=${isPermissionValid}&RelayState=/?isCustomDomain=${isCustomDomain}`;
      this.window.open(`${this.userSettingsEndpoints.dXeHelpURL}?${queryParam}`);
    });
  }

  public navigateToApiTokens(): void {
    this.router.navigate([`/${this.headerContents.profileRedirectData.myApiAccessTokenRoute}`]);
  }

  public async navigateToRoute(): Promise<void> {
    // Below flag is required to keep track of which route is triggered when user is having unsaved audience changes
    // When click on Save Button from Audience Settings popup window (with unsaved warning message), redirecting to below route.
    let routeName: string;
    const appIntegrationsPermission = await this.ngxPermissionsService.hasPermission(['app-integrations-admin']);
    if (appIntegrationsPermission) {
      routeName = this.headerContents.profileRedirectData.applicationIntegrationsRoute;
    } else {
      const fileUploadsPermissions = await this.ngxPermissionsService.hasPermission(['file-config-read', 'uploads-read']);
      if (fileUploadsPermissions) {
        routeName = this.headerContents.profileRedirectData.applicationIntegrationsRouteFilesPermission;
      } else {
        routeName = this.headerContents.profileRedirectData.applicationIntegrationsRoute;
      }
    }
    this.router.navigate([`/${routeName}`]);
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
